import { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '@/translations';
import type { Language } from '@/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return a default context to prevent errors during initial render
    return {
      language: 'ar' as Language,
      setLanguage: () => {},
      t: (key: string) => key,
      dir: 'rtl' as 'ltr' | 'rtl'
    };
  }
  return context;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'ar' || saved === 'en') ? saved : 'ar';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.title = language === 'ar'
      ? 'جمعية نمط | لتعزيز الحياة الصحية'
      : 'Namat Association | Promoting Healthy Living';
    
    if (language === 'ar') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [language]);

  const t = (key: string, params?: Record<string, string | number>) => {
    try {
      const keys = key.split('.');
      let value: any = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return key;
        }
      }

      if (typeof value !== 'string') {
        return key;
      }

      if (params) {
        // Support both {{param}} and {param} placeholder styles
        let text = value.replace(/\{\{(\w+)\}\}/g, (_, param) => 
          String(params[param] ?? `{{${param}}}`)
        );
        text = text.replace(/\{(\w+)\}/g, (_, param) => 
          String(params[param] ?? `{${param}}`)
        );
        return text;
      }

      return value;
    } catch (error) {
      // console.error('Translation error:', error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        setLanguage, 
        t,
        dir: language === 'ar' ? 'rtl' : 'ltr'
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}